import type { ReactNode } from "react";

import {
  useFeedHealthState,
  useFeedNextUp,
  useNowPlaying,
} from "../../api/hooks.js";
import { Panel } from "../../design-system/surfaces.js";
import { CopyableUrl } from "../../design-system/copyable.js";
import { formatDeliveryFormat } from "../../lib/format.js";
import { BlockedPanel } from "../../player/blocked-notice.js";
import { useFeedPlayback, usePlayer } from "../../player/player-provider.js";
import { PlayButton } from "../../player/transport.js";
import { useFeedContext } from "./feed-detail-layout.js";
import { HealthPanel } from "./health-panel.js";
import { PlaybackTimeline } from "./playback-timeline.js";

/**
 * What is happening on this feed, live: what is on it, what the brain has decided
 * comes after it, what the product has established by listening to it, and what it
 * has actually played. All of it is this feed's own — every read on this screen is
 * addressed to this feed and answers for no other.
 */
export function FeedOverviewTab() {
  const { feed } = useFeedContext();
  const live = useNowPlaying(feed.id);
  const health = useFeedHealthState(feed.id);
  const { detail, play, stop } = usePlayer();
  const { isCurrent, state } = useFeedPlayback(feed.id);
  const airing = live.data?.nowPlaying ?? null;
  const nextUp = useFeedNextUp(feed.id, airing?.historyId ?? null, true);
  const streamUrl = live.data?.streamUrl ?? null;
  const delivery = live.data?.deliveries[0];
  const planned = nextUp.data ?? [];

  return (
    <div className="flex flex-col gap-6">
      <Panel className="p-4 sm:p-5">
        <div className="flex min-w-0 flex-col gap-4">
          <div className="flex flex-col justify-between gap-4 sm:flex-row sm:items-center">
            <div>
              <h2 className="text-sm font-semibold text-ink">Listen live</h2>
              <p className="mt-1 text-xs text-ink-3">
                Monitor what this feed is publishing.
              </p>
            </div>
            <div className="flex items-center gap-3">
              <PlayButton
                disabled={!streamUrl}
                feedName={feed.name}
                onActivate={() => {
                  if (!streamUrl) return;
                  if (
                    isCurrent &&
                    state !== "idle" &&
                    state !== "error" &&
                    state !== "blocked"
                  ) {
                    stop();
                  } else
                    play({
                      feedId: feed.id,
                      feedName: feed.name,
                      streamUrl,
                    });
                }}
                state={state}
              />
              <span className="text-xs text-ink-3">
                {isCurrent && state === "playing"
                  ? "Listening now."
                  : streamUrl
                    ? "Listen to this feed."
                    : "Nothing to listen to yet."}
              </span>
            </div>
          </div>

          {isCurrent && state === "blocked" ? (
            <BlockedPanel streamUrl={detail} />
          ) : null}

          {delivery ? (
            <dl className="grid gap-x-8 gap-y-3 border-t border-border pt-4 text-sm sm:grid-cols-2">
              <Fact label="Delivery">{formatDeliveryFormat(delivery)}</Fact>
              <Fact label="Declared loudness">
                {`${String(live.data?.loudness.integratedLufs ?? 0)} LUFS · ${String(live.data?.loudness.truePeakDbtp ?? 0)} dBTP`}
              </Fact>
            </dl>
          ) : null}

          {streamUrl ? (
            <CopyableUrl label="stream address" value={streamUrl} />
          ) : null}
        </div>
      </Panel>

      {/* Playback is one chronology; health remains beside it because operational
          evidence is a different concern, not another part of the queue. */}
      <div className="grid gap-6 lg:grid-cols-2 lg:items-start">
        <PlaybackTimeline
          feedId={feed.id}
          live={live.data}
          liveError={live.error}
          liveLoading={live.loading}
          planError={nextUp.error}
          planLoading={nextUp.loading}
          planned={planned}
        />

        <HealthPanel health={health.data} loading={health.loading} />
      </div>
    </div>
  );
}

function Fact({ children, label }: { children: ReactNode; label: string }) {
  return (
    <div className="min-w-0">
      <dt className="font-mono text-[0.625rem] tracking-[0.12em] text-ink-3 uppercase">
        {label}
      </dt>
      <dd className="mt-1 min-w-0 text-ink-2">{children}</dd>
    </div>
  );
}
